import { TableCell, TableRow } from '@mui/material';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import logRocketPromise, { LogRocketInstance } from '@/services/logRocket';

interface State {
  readonly error?: Error;
}

interface Props {
  readonly columnCount: number;
  readonly fallbackMessage?: string;
}

export class TableRowErrorBoundary extends React.Component<React.PropsWithChildren<Props>, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: undefined };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error: error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('TableRowErrorBoundary caught an error:', error, errorInfo);
    void logRocketPromise.then((logRocket: LogRocketInstance): void => {
      logRocket.captureException(error, {
        extra: {
          boundary: 'TableRowErrorBoundary',
          componentStack: errorInfo.componentStack ?? '',
          source: 'react_error_boundary',
        },
      });
    });
  }

  render(): React.ReactNode {
    const { fallbackMessage, columnCount, children } = this.props;
    const { error } = this.state;

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={columnCount} sx={styles.cell}>
            <Typography variant="body2" color="error">
              {fallbackMessage ?? 'Error displaying row data'}
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return children;
  }
}

const styles: Record<string, SystemStyleObject> = {
  cell: {
    padding: 2,
    textAlign: 'center',
  },
};
